<template>
  <v-container class="password-page py-6" fluid>
    <v-row justify="center">
      <v-col cols="12" sm="9" md="7" lg="6" xl="5">
        <v-card class="password-card" elevation="2">
          <!-- Header -->
          <div class="password-header">
            <div class="password-title-block">
              <div class="password-title-icon">
                <v-icon color="primary" size="28">
                  mdi-lock-reset
                </v-icon>
              </div>

              <div>
                <div class="text-h6 font-weight-bold">
                  Passwort ändern
                </div>

                <div class="text-body-2 text-medium-emphasis">
                  Aktualisieren Sie das Passwort für Ihr Benutzerkonto.
                </div>
              </div>
            </div>
          </div>

          <v-divider />

          <v-card-text class="password-card-content">
            <v-alert
              type="info"
              variant="tonal"
              border="start"
              class="password-hint"
            >
              Verwenden Sie ein Passwort mit mindestens acht Zeichen und
              vermeiden Sie leicht zu erratende Begriffe.
            </v-alert>

            <v-form
              ref="formRef"
              v-model="valid"
              class="password-form"
              @submit.prevent="onSubmit"
            >
              <!-- Current password -->
              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-lock-outline
                  </v-icon>

                  <span>Aktuelles Passwort</span>
                </div>

                <v-text-field
                  v-model="oldPassword"
                  :type="showOld ? 'text' : 'password'"
                  :rules="[requiredRule]"
                  :error-messages="fieldErrors.old_password"
                  label="Altes Passwort"
                  autocomplete="current-password"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-inner-icon="showOld ? 'mdi-eye-off' : 'mdi-eye'"
                  :disabled="saving"
                  validate-on="blur"
                  class="mb-1"
                  @click:append-inner="showOld = !showOld"
                  @update:model-value="clearFieldError('old_password')"
                />
              </div>

              <!-- New password -->
              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-lock-plus-outline
                  </v-icon>

                  <span>Neues Passwort</span>
                </div>

                <v-text-field
                  v-model="newPassword"
                  :type="showNew ? 'text' : 'password'"
                  :rules="[requiredRule, minLenRule]"
                  :error-messages="fieldErrors.new_password"
                  label="Neues Passwort"
                  autocomplete="new-password"
                  prepend-inner-icon="mdi-lock-plus-outline"
                  :append-inner-icon="showNew ? 'mdi-eye-off' : 'mdi-eye'"
                  :disabled="saving"
                  validate-on="blur"
                  class="mb-3"
                  @click:append-inner="showNew = !showNew"
                  @update:model-value="clearFieldError('new_password')"
                />

                <v-text-field
                  v-model="confirmPassword"
                  :type="showConfirm ? 'text' : 'password'"
                  :rules="[requiredRule, minLenRule, matchRule]"
                  :error-messages="fieldErrors.confirm_password"
                  label="Neues Passwort wiederholen"
                  autocomplete="new-password"
                  prepend-inner-icon="mdi-lock-check-outline"
                  :append-inner-icon="showConfirm ? 'mdi-eye-off' : 'mdi-eye'"
                  :disabled="saving"
                  validate-on="blur"
                  class="mb-1"
                  @click:append-inner="showConfirm = !showConfirm"
                  @update:model-value="clearFieldError('confirm_password')"
                />
              </div>
            </v-form>
          </v-card-text>

          <v-divider />

          <!-- Footer -->
          <div class="password-footer">
            <v-btn
              variant="text"
              :disabled="saving"
              @click="goBack"
            >
              Abbrechen
            </v-btn>

            <v-btn
              color="primary"
              variant="elevated"
              :loading="saving"
              :disabled="!valid || saving"
              class="submit-button"
              @click="onSubmit"
            >
              <v-icon start>mdi-content-save-check-outline</v-icon>
              Passwort ändern
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<!-- <script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/plugins/axios' // your axios instance with baseURL '/api/v1'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

// State
const router = useRouter()
const formRef = ref(null)
const valid = ref(false)
const saving = ref(false)

// Fields
const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

// Show/Hide toggles
const showOld = ref(false)
const showNew = ref(false)
const showConfirm = ref(false)

// Backend field errors map (driven by DRF 400 response)
const fieldErrors = ref({
  old_password: [],
  new_password: [],
  confirm_password: [],
})

// Rules
const requiredRule = (v) => (!!v || 'Dieses Feld muss ausgefüllt sein.')
const minLenRule = (v) =>
  (!v || v.length >= 8 || 'Das Passwort muss mindestens 8 Zeichen lang sein.')
const matchRule = (v) =>
  (v === newPassword.value || 'Die Passwörter stimmen nicht überein.')

// No get method defined in the backend, so we skip fetching any metadata for now
// async function fetchPasswordMeta() {
//   try {
//     await api.get('/password')
//     // handle data if needed
//   } catch (e) {
//     // ignore or log
//   }
// }
// onMounted(fetchPasswordMeta)

// Helpers
function resetFieldErrors() {
  fieldErrors.value = {
    old_password: [],
    new_password: [],
    confirm_password: [],
  }
}

// Submit
async function onSubmit() {
  // Client-side validate first
  const form = formRef.value
  if (form) {
    const res = await form.validate()
    if (!res?.valid) return
  }

  resetFieldErrors()
  saving.value = true
  try {
    const payload = {
      old_password: oldPassword.value,
      new_password: newPassword.value,
      confirm_password: confirmPassword.value,
    }
    await api.post('/password/change_password/', payload)

    toast.success('Passwort erfolgreich geändert.')
    // Optionally, force re-login if your backend invalidates tokens/sessions on password change
    // await logout()
    router.push({ name: 'Home' })
  } catch (err) {
    const status = err?.response?.status
    const data = err?.response?.data || {}
    if (status === 400) {
      // Map backend field errors to the inputs
      // DRF typically returns: { field_name: ["message", ...], ... }
      if (data.old_password) {
        fieldErrors.value.old_password = Array.isArray(data.old_password)
          ? data.old_password
          : [String(data.old_password)]
        toast.error('Das eingegebene alte Passwort ist falsch.')
      }
      if (data.confirm_password) {
        fieldErrors.value.confirm_password = Array.isArray(data.confirm_password)
          ? data.confirm_password
          : [String(data.confirm_password)]
        toast.error('Passwörter stimmen nicht überein.')
      }
      if (data.new_password) {
        fieldErrors.value.new_password = Array.isArray(data.new_password)
          ? data.new_password
          : [String(data.new_password)]
      }

      // If there are non-field errors
      if (data.non_field_errors) {
        const msgs = Array.isArray(data.non_field_errors)
          ? data.non_field_errors
          : [String(data.non_field_errors)]
        msgs.forEach((m) => toast.error(m))
      }
    } else {
      toast.error('Es gab einen Fehler beim Ändern des Passworts.')
    }
  } finally {
    saving.value = false
  }
}
</script> -->

<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/plugins/axios'
import 'vue3-toastify/dist/index.css'

const router = useRouter()

// Strukturell statt `VForm` aus Vuetify: gebraucht wird hier nur `validate`,
// und der Aufruf unten ist ohnehin gegen ein fehlendes Formular abgesichert.
const formRef = ref<{ validate?: () => Promise<{ valid: boolean }> } | null>(null)
const valid = ref(false)
const saving = ref(false)

const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

const showOld = ref(false)
const showNew = ref(false)
const showConfirm = ref(false)

type FieldName = 'old_password' | 'new_password' | 'confirm_password'

const fieldErrors = ref<Record<FieldName, string[]>>({
  old_password: [],
  new_password: [],
  confirm_password: [],
})

const requiredRule = (value: string) =>
  !!value || 'Dieses Feld muss ausgefüllt sein.'

const minLenRule = (value: string) =>
  !value ||
  value.length >= 8 ||
  'Das Passwort muss mindestens 8 Zeichen lang sein.'

const matchRule = (value: string) =>
  value === newPassword.value ||
  'Die Passwörter stimmen nicht überein.'

function resetFieldErrors() {
  fieldErrors.value = {
    old_password: [],
    new_password: [],
    confirm_password: [],
  }
}

function clearFieldError(field: FieldName) {
  fieldErrors.value[field] = []
}

function normalizeErrors(value: unknown): string[] {
  if (!value) return []
  return Array.isArray(value) ? value.map(String) : [String(value)]
}

function goBack() {
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push({ name: 'Home' })
  }
}

async function onSubmit() {
  if (saving.value) return

  const result = await formRef.value?.validate?.()

  if (!result?.valid) {
    toast.warning('Bitte überprüfen Sie Ihre Eingaben.')
    return
  }

  resetFieldErrors()
  saving.value = true

  try {
    await api.post('/password/change_password/', {
      old_password: oldPassword.value,
      new_password: newPassword.value,
      confirm_password: confirmPassword.value,
    })

    toast.success('Passwort erfolgreich geändert.')
    router.push({ name: 'Home' })
  } catch (err) {
    // `err` ist im catch `unknown`; die Fehlerantwort des Servers ist ein
    // freies Objekt aus Feldname -> Meldungen, das `normalizeErrors` prueft.
    const failure = err as {
      response?: { status?: number; data?: Record<string, unknown> }
    }
    const status = failure.response?.status
    const data = failure.response?.data || {}

    if (status === 400) {
      fieldErrors.value.old_password = normalizeErrors(
        data.old_password
      )

      fieldErrors.value.new_password = normalizeErrors(
        data.new_password
      )

      fieldErrors.value.confirm_password = normalizeErrors(
        data.confirm_password
      )

      if (data.old_password) {
        toast.error('Das eingegebene alte Passwort ist nicht korrekt.')
      }

      if (data.confirm_password) {
        toast.error('Die Passwörter stimmen nicht überein.')
      }

      const nonFieldErrors = normalizeErrors(
        data.non_field_errors
      )

      nonFieldErrors.forEach(message => {
        toast.error(message)
      })

      if (
        !data.old_password &&
        !data.new_password &&
        !data.confirm_password &&
        !data.non_field_errors
      ) {
        toast.error('Bitte überprüfen Sie Ihre Eingaben.')
      }
    } else {
      toast.error(
        'Das Passwort konnte nicht geändert werden.'
      )
    }
  } finally {
    saving.value = false
  }
}
</script>

<style scoped lang="scss">
.password-page {
  min-height: 100%;
}

.password-card {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 28px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

.password-header {
  padding: 1.25rem 1.5rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.password-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.password-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

.password-card-content {
  display: grid;
  gap: 1.25rem;
  padding: 1.5rem;
}

.password-hint {
  border-radius: 20px;
}

.password-form {
  display: grid;
  gap: 1.25rem;
}

.form-section {
  padding: 1rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;

  background-color: rgba(var(--v-theme-primary), 0.025);
}

.form-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

.password-form :deep(.v-field) {
  border-radius: 18px;
}

.password-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;

  padding: 1rem 1.5rem;
}

.password-footer .v-btn {
  font-weight: 600;
  text-transform: none;
}

.submit-button {
  min-width: 180px;
}

@media (max-width: 700px) {
  .password-page {
    padding-inline: 0.5rem;
  }

  .password-header {
    padding: 1rem;
  }

  .password-title-block {
    align-items: flex-start;
  }

  .password-title-icon {
    width: 42px;
    height: 42px;
  }

  .password-card-content {
    padding: 1rem;
  }

  .form-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .password-footer {
    align-items: stretch;
    flex-direction: column-reverse;
    padding: 1rem;
  }

  .password-footer .v-btn {
    width: 100%;
  }

  .submit-button {
    min-width: 0;
  }
}
</style>